import { LocationQueryValue } from "vue-router";
import Cookies from "universal-cookie";
import { v4 as uuidv4 } from "uuid";
import { SLUG_COMPANY_ID_MAP, NOT_FOUND_COMPANY_ID } from "@/constants/companyNames";
import { z } from "zod";
import { PensionFund } from "@/stores/application";

export const isNumber = (event: KeyboardEvent) => {
    const allowedKeys = [
        "1", "2", "3", "4", "5", "6", "7", "8", "9", "0",
        "Backspace", "ArrowLeft", "ArrowRight", "Tab", "Enter",
    ];
    if (!allowedKeys.includes(event.key)) {
        event.preventDefault();
    } else {
        return true;
    }
};

export const isDecimal = (event: KeyboardEvent | ClipboardEvent) => {
    const input = event.target;
    if (input instanceof HTMLInputElement) {
        if (event instanceof KeyboardEvent) {
            const key = event.key;
            const value = input.value;
            if (
                (!/[\d.]/.test(key) && !["Backspace", "ArrowLeft", "ArrowRight", "Tab", "Enter"].includes(key)) ||
                (key === "." && value.includes(".")) ||
                (key === "-" && value.includes("-"))
            ) {
                event.preventDefault();
            } else {
                return true;
            }
        } else if (event instanceof ClipboardEvent) {
            const pasteData = event.clipboardData?.getData("text") || "";
            if (pasteData.includes("-") || pasteData.includes("e")) {
                event.preventDefault();
            } else {
                return true;
            }
        }
    }
};
export function throttle(delay: number, fn: (...args: unknown[]) => void) {
    let lastCall = 0;
    return function(...args: unknown[]) {
        const now = new Date().getTime();
        if (now - lastCall < delay) {
            return;
        }
        lastCall = now;
        return fn(...args);
    };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce(func: (...args: any[]) => void | Promise<void>, delay: number) {
    let timeoutId: NodeJS.Timeout;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function(...args: any[]) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            func(...args);
        }, delay);
    };
}

export const groupBy = <T, K extends string>(array: T[], predicate: (value: T) => K) => {
    const result: Partial<Record<K, Array<T>>> = {};
    for (const item of array) {
        const key = predicate(item);
        (result[key] ??= []).push(item);
    }
    return result as Record<K, Array<T>>;
};

export const ValueOrUndefined =
<T>(value: T, defaultValue: T): T | undefined => {
    return value === defaultValue ? undefined : value;
};

export const toTitleCase = (text: string) => {
    if (text === "") {
        return "";
    }
    return text.split(" ").map((x: string) => {
        return x[0].toUpperCase() + x.slice(1).toLowerCase();
    }).join(" ");
};

export const normalizeText = (text: string) => {
    return text.toLowerCase().replaceAll(" ", "");
};

export const jsonParse = (data: string): unknown => JSON.parse(data);

export const numbersArray = (to = 0, from = 0) => Array.from({ length: (to - from) + 1 }, (_, i) => i);

export const getNumberOptions = (i: number) => ({
    value: i,
    label: `${i}`,
});

export const formatOptions = (input?: [string, string][]) => input?.map((i) => ({
    value: i[1],
    label: i[0],
}));

export const getImageUrl = (svg: string) => {
    return new URL(`../assets/images/${svg}`, import.meta.url).href;
};

export function isTokenInvalid(token: string): boolean {
    try {
        return Date.now() >= (JSON.parse(atob(token.split(".")[1]))).exp * 1000;
    } catch {
        return true;
    }
}

export function parseQueryQuoteNumber(quoteNumber: LocationQueryValue | LocationQueryValue[]): string | undefined {
    if (Array.isArray(quoteNumber)) {
        return quoteNumber[0] || undefined;
    } else {
        return quoteNumber || undefined;
    }
}

export const getCompanyIdBySlug = (slug?: string) => {
    if (!slug) {
        return NOT_FOUND_COMPANY_ID;
    }

    return SLUG_COMPANY_ID_MAP[slug] || NOT_FOUND_COMPANY_ID;
};

// OPTIONCHOICE_ID matches travel
export const getSessionId = (): string => {
    const cookies = new Cookies();
    let sessionId = cookies.get("OPTIONCHOICE_ID");
    if (!sessionId) {
        sessionId = uuidv4();
        cookies.set("OPTIONCHOICE_ID", sessionId, { path: "/" });
    }

    return z.string().parse(sessionId);
};

export const isBot = (): boolean => {
    return Array.isArray(navigator.userAgent.match(/(adsbot-google|statuscake|uptime)/i)) || navigator.webdriver;
};

export const getTaxFreeCash = (fund: PensionFund) => {
    if (fund.maxTaxFreeCash) {
        return fund.totalFund * 0.25;
    }
    return fund.taxFreeCashAmount;
};
